import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Copy, RotateCcw, ArrowRightLeft, Settings, BookOpen } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const CaesarCipherTool: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [shift, setShift] = useState(3);
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [preserveCase, setPreserveCase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(false);

  useSEO({
    title: 'Caesar Cipher Tool - Encode & Decode with Custom Shift',
    description: 'Encrypt and decrypt text using Caesar cipher with customizable shift values. Classic substitution cipher used by Julius Caesar. Free online tool.',
    keywords: 'caesar cipher, caesar cipher tool, shift cipher, substitution cipher, encryption, decryption, julius caesar',
    canonical: 'https://snap-tools.online/security/caesar-cipher-tool'
  });

  const caesarTransform = (text: string, shiftValue: number, isEncoding: boolean): string => {
    const actualShift = isEncoding ? shiftValue : -shiftValue;
    
    return text.replace(/[A-Za-z0-9]/g, (char) => {
      if (/[A-Za-z]/.test(char)) {
        // Handle letters
        const isUpperCase = char === char.toUpperCase();
        const start = isUpperCase ? 65 : 97; // ASCII value for 'A' or 'a'
        const code = char.charCodeAt(0);
        const shifted = ((code - start + actualShift + 26) % 26) + start;
        const result = String.fromCharCode(shifted);
        return preserveCase ? result : (isUpperCase ? result.toUpperCase() : result.toLowerCase());
      } else if (includeNumbers && /[0-9]/.test(char)) {
        // Handle numbers (0-9)
        const code = char.charCodeAt(0);
        const shifted = ((code - 48 + actualShift + 10) % 10) + 48;
        return String.fromCharCode(shifted);
      }
      return char;
    });
  };

  const handleConvert = () => {
    if (!inputText.trim()) {
      toast.error('Please enter text to convert');
      return;
    }

    if (shift < 1 || shift > 25) {
      toast.error('Shift value must be between 1 and 25');
      return;
    }

    const result = caesarTransform(inputText, shift, mode === 'encode');
    setOutputText(result);
    toast.success(`Text ${mode === 'encode' ? 'encoded' : 'decoded'} successfully`);
  };

  const tryAllShifts = () => {
    if (!inputText.trim()) {
      toast.error('Please enter text to analyze');
      return;
    }

    let allResults = 'All possible Caesar cipher shifts:\n\n';
    for (let i = 1; i <= 25; i++) {
      const result = caesarTransform(inputText, i, false); // Always decode for analysis
      allResults += `Shift ${i}: ${result}\n`;
    }
    setOutputText(allResults);
    toast.success('Generated all possible shifts');
  };

  const swap = () => {
    setInputText(outputText);
    setOutputText(inputText);
    setMode(mode === 'encode' ? 'decode' : 'encode');
  };

  const copyToClipboard = async (text: string, type: string) => {
    if (!text) {
      toast.error(`No ${type} to copy`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const reset = () => {
    setInputText('');
    setOutputText('');
    setShift(3);
    setMode('encode');
  };

  // Historical examples
  const historicalExamples = [
    { 
      original: 'ATTACK AT DAWN',
      encoded: 'DWWDFN DW GDZQ',
      shift: 3,
      description: 'Classic Caesar cipher with shift of 3'
    },
    {
      original: 'VENI VIDI VICI',
      encoded: 'YHQL YLGL YLFL',
      shift: 3,
      description: 'Julius Caesar\'s famous quote'
    },
    {
      original: 'THE QUICK BROWN FOX',
      encoded: 'QEB NRFZH YOLTK CLU',
      shift: 3,
      description: 'Standard test phrase'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Crown className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Caesar Cipher Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Encrypt and decrypt text using the Caesar cipher with customizable shift values. This ancient encryption method was used by Julius Caesar to protect military communications.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Mode Selection */}
          <div className="flex justify-center mb-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setMode('encode')}
                className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                  mode === 'encode' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Encrypt
              </button>
              <button
                onClick={() => setMode('decode')}
                className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                  mode === 'decode' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Decrypt
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Cipher Settings</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shift Value: {shift}
                </label>
                <input
                  type="range"
                  min="1"
                  max="25"
                  value={shift}
                  onChange={(e) => setShift(parseInt(e.target.value))}
                  className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1</span>
                  <span className="font-semibold text-purple-600">Current: {shift}</span>
                  <span>25</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Preserve case</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Include numbers (0-9)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div className="space-y-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter text to {mode === 'encode' ? 'encrypt' : 'decrypt'}:
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={`Type your ${mode === 'encode' ? 'message' : 'encrypted text'} here...`}
                className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={handleConvert}
                disabled={!inputText.trim()}
                className="bg-purple-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mode === 'encode' ? 'Encrypt' : 'Decrypt'}
              </button>
              
              <button
                onClick={tryAllShifts}
                disabled={!inputText.trim()}
                className="bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Try All Shifts
              </button>
              
              <button
                onClick={swap}
                disabled={!outputText}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Swap input and output"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Swap
              </button>
              
              <button
                onClick={reset}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>

          {/* Output Section */}
          {outputText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {outputText.includes('All possible') ? 'Cryptanalysis Results:' : 'Result:'}
                </h3>
                <button
                  onClick={() => copyToClipboard(outputText, 'result')}
                  className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
              
              <div className="bg-white rounded border p-4">
                <pre className="whitespace-pre-wrap font-mono text-sm">{outputText}</pre>
              </div>
            </motion.div>
          )}

          {/* Historical Examples */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-yellow-600" />
              <h4 className="font-semibold text-yellow-900">Historical Examples</h4>
            </div>
            <div className="space-y-4">
              {historicalExamples.map((example, index) => (
                <div key={index} className="bg-white rounded p-4">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 mb-1">Original:</div>
                    <div className="text-gray-700 mb-2 font-mono">{example.original}</div>
                    <div className="font-medium text-yellow-900 mb-1">Encrypted (Shift {example.shift}):</div>
                    <div className="text-yellow-700 font-mono mb-2">{example.encoded}</div>
                    <div className="text-xs text-gray-600 italic">{example.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">About Caesar Cipher</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Ancient Origin:</strong> Used by Julius Caesar around 50 BC</li>
                <li>• <strong>Simple Method:</strong> Each letter is shifted by a fixed number</li>
                <li>• <strong>Military Use:</strong> Protected Roman military communications</li>
                <li>• <strong>Shift of 3:</strong> Caesar typically used a shift of 3</li>
                <li>• <strong>Monoalphabetic:</strong> Each letter always maps to the same letter</li>
                <li>• <strong>Educational Value:</strong> Great introduction to cryptography</li>
              </ul>
            </div>
            
            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Cryptanalysis & Security</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• <strong>Easily Broken:</strong> Only 25 possible keys to try</li>
                <li>• <strong>Frequency Analysis:</strong> Letter patterns remain visible</li>
                <li>• <strong>Brute Force:</strong> Can be broken in seconds by computer</li>
                <li>• <strong>No Security:</strong> Should never be used for real encryption</li>
                <li>• <strong>Historical Interest:</strong> Important step in cryptographic evolution</li>
                <li>• <strong>Learning Tool:</strong> Helps understand basic cipher principles</li>
              </ul>
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-6 bg-green-50 rounded-lg p-6">
            <h4 className="font-semibold text-green-900 mb-3">How Caesar Cipher Works</h4>
            <div className="text-sm text-green-800 space-y-2">
              <p>
                <strong>Encryption:</strong> Replace each letter with the letter that is a fixed number of positions later in the alphabet.
              </p>
              <p>
                <strong>Example with shift 3:</strong> A→D, B→E, C→F, ... X→A, Y→B, Z→C
              </p>
              <p>
                <strong>Wraparound:</strong> When reaching the end of the alphabet, it wraps around to the beginning.
              </p>
              <p>
                <strong>Decryption:</strong> Reverse the process by shifting backwards by the same amount.
              </p>
              <p>
                <strong>Key Space:</strong> Only 25 possible keys (shifts 1-25), making it very weak.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CaesarCipherTool;
